// @ts-nocheck - formatter / Table column Render types
import React, { useState } from 'react';
import {
  loadKeyPools,
  exportKeyPoolsData,
} from '../../../actions/metricActions';
import {
  Table,
  SimpleTextFilter,
  SimpleDropdownFilter,
  getVisibleColumns,
} from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import AutoSelectText from '../../common/AutoSelectText';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import useUploadNotifications from '../../../hooks/useUploadNotifications';
import TransactionStatusBadge from './TransactionStatusBadge';
import { hasPermission } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import KebabMenu from '../../common/KebabMenu';
import Button from '../../common/Button';
import icons from '../../../constants/icons';
import GameLink from '../../common/links/GameLink';
import WorkflowLink from '../../common/links/WorkflowLink';
import skuHelp from '../../../constants/help/skuHelp';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppSelector } from '../../../hooks';

const rangeFilterOptions = [
  { value: '{"min": 0, "max": 0}', label: '0' },
  { value: '{"min": 1}', label: '> 0' },
  { value: '{"min": 0, "max": 1000}', label: '0 - 1k' },
  { value: '{"min": 1000, "max": 100000}', label: '1k - 10k' },
  { value: '{"min": 10001 }', label: '> 10k' },
];

export interface KeyPoolsListProps {
  reload?: any;
  upload: (e: any, item: any) => void;
  importTransactions?: any;
  onDisableUnused: (e: any, item: any) => void;
}

function KeyPoolsList({ reload, upload, importTransactions, onDisableUnused }: KeyPoolsListProps) {
  const location = useLocation();
  const language = i18next.language;
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.auth);

  const suffixColumns = ['skuId'];
  const defaultVisibleColumns = [
    'gameName',
    'packageId',
    'keyWorkflowName',
    'available',
    'used',
    'total',
  ];
  const allColumns = [...defaultVisibleColumns, 'disabled'];
  const [keypools, setKeyPools] = useState<any>();
  useUploadNotifications({
    keypools,
    setKeyPools,
    importTransactions,
  });
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'keypoolslist-settings',
    getTableSettingsDefaults({
      defaultSort: 'gameName',
      defaultVisibleColumns,
    }),
    null,
    loadKeyPools,
    setKeyPools,
    reload,
    t('Failed to load key pools')
  );

  let columnsConfig = [
    {
      Key: 'gameName',
      Label: t('Game'),
      width: '20%',
      Render: (item: any) => (
        <>
          <GameLink
            gameId={item.gameId}
            skuId={item.skuId}
            gameTitle={item.gameName}
            skuName={item.skuName}
            key={item.moveTransactionId}
            currentUser={currentUser}
          />
          {(item.skuIsArchived || item.gameIsArchived) && (
            <div className="archived mt-2">
              <span className="badge bg-danger">
                <FontAwesomeIcon icon={icons.ARCHIVED} className="me-2" />
                {t('Archived')}
              </span>
            </div>
          )}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'packageId',
      Label: t('Package Id'),
      className: 'autoselect',
      Render: (item: any) => <AutoSelectText>{item.packageId}</AutoSelectText>,
      width: '15%',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyWorkflowName',
      Label: t('Pool'),
      width: '20%',
      Render: (item: any) => (
        <>
          <WorkflowLink
            id={item.keyWorkflowId}
            key={item.keyWorkflowId}
            name={item.keyWorkflowName}
            colour={item.keyWorkflowColourHex}
          />
          {item.keyWorkflowIsDeleted && (
            <span className="badge bg-danger">{t('Deleted')}</span>
          )}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'available',
      Label: t('Pooled'),
      helpContent: skuHelp.SKU_POOL_TABLE_POOLED[language],
      className: 'text-right num no-wrap',
      width: '15%',
      Render: (row: any) =>
        row.lowStock ? (
          <span className="badge bg-danger">
            {CommaNumber_NoDecimal(row.available)}
          </span>
        ) : (
          CommaNumber_NoDecimal(row.available)
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleDropdownFilter
            Key={col.Key}
            value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
            options={rangeFilterOptions}
            placeholder="not set"
          />
        ),
      },
    },
    {
      Key: 'used',
      Label: t('Delivered'),
      helpContent: skuHelp.SKU_POOL_TABLE_DELIVERED[language],
      className: 'text-right num no-wrap',
      width: '15%',
      Render: (row: any) => CommaNumber_NoDecimal(row.used),
      Filter: {
        Render: (col: any) => (
          <SimpleDropdownFilter
            Key={col.Key}
            value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
            options={rangeFilterOptions}
            placeholder="not set"
          />
        ),
      },
    },
    {
      Key: 'total',
      Label: t('Total'),
      helpContent: skuHelp.SKU_POOL_TABLE_TOTAL[language],
      className: 'text-right num no-wrap',
      width: '15%',
      Render: (row: any) => CommaNumber_NoDecimal(row.total),
      Filter: {
        Render: (col: any) => (
          <SimpleDropdownFilter
            Key={col.Key}
            value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
            options={rangeFilterOptions}
            placeholder="not set"
          />
        ),
      },
    },
    {
      Key: 'disabled',
      Label: t('Disabled'),
      className: 'text-right num no-wrap',
      width: '15%',
      Render: (row: any) => CommaNumber_NoDecimal(row.disabled),
      Filter: {
        Render: (col: any) => (
          <SimpleDropdownFilter
            Key={col.Key}
            value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
            options={rangeFilterOptions}
            placeholder="not set"
          />
        ),
      },
    },
    {
      Key: 'skuId',
      Label: '',
      className: 'text-right no-navigate no-wrap',
      Sort: false,
      width: '20%',
      Render: (item: any) =>
        item.uploadInProgress ? (
          <TransactionStatusBadge
            success={item.success}
            partialSuccess={item.partialSuccess}
            message={item.message}
            status={item.status}
          />
        ) : (
          item.canUploadKeys && (
            <>
              <KebabMenu
                take={1}
                className="inline-icons"
                buttons={[
                  <Button
                    key={item.id + 'upload'}
                    className="btn btn-default me-2"
                    onClick={(e) => upload(e, item)}
                    icon={icons.UPLOAD_KEYS}
                    text={t('Upload')}
                  />,
                  hasPermission(currentUser, permissionTypes.MANAGE_KEYS) && (
                    <Button
                      key={item.id + 'disable'}
                      className="btn btn-default me-2"
                      isDisabled={!item.available || item.available === 0}
                      onClick={(e) => onDisableUnused(e, item)}
                      icon={icons.DISABLE_KEYS}
                      text={t('Disable unused')}
                    />
                  ),
                ]}
              />
            </>
          )
        ),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return keypools ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={keypools.data.map((x: any) => ({ Key: `${x.skuId}-${x.keyWorkflowId}`, ...x }))}
        totalRowCount={keypools.totalRowCount}
        filteredRowCount={keypools.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportKeyPoolsData}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default KeyPoolsList;
